import { yupResolver } from '@hookform/resolvers/yup';
import { useMutation } from '@tanstack/react-query';
import classNames from 'classnames';
import { useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import * as Yup from 'yup';
import { ServiceDto, UpsertServiceDto, useClients } from 'src/api';
import { FormAlert, Forms, Modal } from 'src/components';
import { texts } from 'src/texts';

const SCHEME = Yup.object({
  // Required name.
  name: Yup.string().label(texts.common.name).required(),

  // Optional description.
  description: Yup.string().label(texts.common.description).required(),

  // Required currency.
  currency: Yup.string().label(texts.common.currency).required(),

  // Required non-negative price.
  pricePerCoreHour: Yup.number().label(texts.services.pricePerCoreHour).required().min(0),

  // Required non-negative price.
  pricePerVolumeGBHour: Yup.number().label(texts.services.pricePerVolumeGBHour).required().min(0),

  // Required non-negative price.
  pricePerMemoryGBHour: Yup.number().label(texts.services.pricePerMemoryGBHour).required().min(0),

  // Required non-negative price.
  pricePerStorageGBMonth: Yup.number().label(texts.services.pricePerStorageGBMonth).required().min(0),

  // Required non-negative price.
  fixedPrice: Yup.number().label(texts.services.fixedPrice).required().min(0),
});

const RESOLVER = yupResolver<any>(SCHEME);

const DEFAULT_VALUE: Partial<ServiceDto> = {
  currency: 'USD',
  fixedPrice: 0,
  isPublic: true,
  pricePerCoreHour: 0,
  pricePerMemoryGBHour: 0,
  pricePerStorageGBMonth: 0,
  pricePerVolumeGBHour: 0,
};

export interface UpsertServiceDialogProps {
  // The service.
  target?: ServiceDto;

  // Invoked when cancelled.
  onClose: () => void;

  // Invoked when created.
  onUpsert: (service: ServiceDto) => void;
}

export const UpsertServiceDialog = (props: UpsertServiceDialogProps) => {
  const { onClose, onUpsert, target } = props;
  const clients = useClients();
  const [activeTab, setActiveTab] = useState(0);

  const upserting = useMutation({
    mutationFn: (request: UpsertServiceDto) => {
      request.environment ||= {};
      if (target) {
        return clients.services.putService(target.id, request);
      } else {
        return clients.services.postService(request);
      }
    },
    onSuccess: (response) => {
      onUpsert(response);
      onClose();
    },
  });

  const form = useForm<UpsertServiceDto>({ resolver: RESOLVER, defaultValues: target || DEFAULT_VALUE });

  return (
    <FormProvider {...form}>
      <form onSubmit={form.handleSubmit((v) => upserting.mutate(v))}>
        <Modal
          fullHeight
          size="lg"
          onClose={onClose}
          header={
            <>
              <div className="flex items-center gap-4">{target ? texts.services.update : texts.services.create}</div>

              <div role="tablist" className="tabs tabs-md tabs-border -ms-4 mt-4 -mb-6">
                <a role="tab" className={classNames('tab', { 'tab-active': activeTab === 0 })} onClick={() => setActiveTab(0)}>
                  {texts.common.common}
                </a>
                <a role="tab" className={classNames('tab', { 'tab-active': activeTab === 1 })} onClick={() => setActiveTab(1)}>
                  {texts.common.environment}
                </a>
              </div>
            </>
          }
          footer={
            <fieldset disabled={upserting.isPending}>
              <div className="flex flex-row justify-between">
                <button type="button" className="btn btn-ghost" onClick={onClose}>
                  {texts.common.cancel}
                </button>

                <button type="submit" className="btn btn-primary">
                  {texts.common.save}
                </button>
              </div>
            </fieldset>
          }
        >
          <fieldset disabled={upserting.isPending}>
            {activeTab === 0 ? (
              <>
                <FormAlert common={target ? texts.services.updateFailed : texts.services.createFailed} error={upserting.error} />

                <Forms.Text name="name" label={texts.common.name} required />

                <Forms.Boolean name="isPublic" label={texts.services.isPublic} required />

                <Forms.Markdown name="description" label={texts.common.description} required />

                <section>
                  <legend className="legend">{texts.services.pricing}</legend>

                  <Forms.Text name="currency" label={texts.common.currency} maxLength={3} required />

                  <Forms.Number name="pricePerCoreHour" label={texts.services.pricePerCoreHour} required />

                  <Forms.Number name="pricePerVolumeGBHour" label={texts.services.pricePerVolumeGBHour} required />

                  <Forms.Number name="pricePerMemoryGBHour" label={texts.services.pricePerMemoryGBHourLabel} required />

                  <Forms.Number name="pricePerStorageGBMonth" label={texts.services.pricePerStorageGBMonthLabel} required />

                  <Forms.Number name="fixedPrice" label={texts.services.fixedPrice} required />
                </section>
              </>
            ) : (
              <Forms.Code height="full" label="" mode="javascript" name="environment" valueMode="object" vertical />
            )}
          </fieldset>
        </Modal>
      </form>
    </FormProvider>
  );
};
